/**
 * AI summary request assembler (#719).
 *
 * Builds the `POST /scans/summarize` body from AI Scanner flow state. Pure —
 * every input is passed in, including `localize`, so this is unit-testable
 * without a React tree or a store.
 *
 * The contract's hard rules, all enforced here:
 *  - every `value` is a STRING (array of strings for `in`) — numbers are a 400
 *  - every scan carries an English `scan` id AND a JP `label`
 *  - no raw Syphon codes go over the wire (`166,0111` → `東証プライム`)
 *  - `matched[]` OMITS scans with no value — never `null`, `0`, or `"N/A"`
 *  - every question needs a non-empty `id` and `question`
 *
 * @see src/constants/summarizeDisplayMap.js for the code→label/unit table.
 */

import Logger from './logger';
import HASHTAG_FILTER_CONFIG from '../constants/hashtagFilterConfig';
import INVESTMENT_QUESTIONS from '../constants/investmentQuestions';
import SCAN_DISPLAY, {
  OPERATOR_MAP,
  POSITIVE_FORM
} from '../constants/summarizeDisplayMap';
import { HASHTAG_DEFINITIONS } from '../views/scan/HashtagChips';

/** Syphon caps `symbols[]` at 5 (`MAX_SYMBOLS`). */
export const MAX_SUMMARY_SYMBOLS = 5;

/**
 * Leading `Q1. ` / `A. ` markers are part of the on-screen label per the Figma
 * design, but they are display chrome — Syphon's sample carries the bare
 * question and option text, so they are stripped before sending.
 */
const QUESTION_PREFIX = /^Q\d+[.．]\s*/;
const OPTION_PREFIX = /^[A-Z][.．]\s*/;

/**
 * The chip labels are stored with a space after the hash (`# 毎日使う身近な企業`)
 * for on-screen spacing, but a hashtag in prose has none — Syphon's own sample
 * uses `#毎日使う身近な企業`. Normalized so the prompt reads naturally.
 */
const TAG_HASH_SPACING = /^#[\s\u3000]+/;

/**
 * `localize` returns the key itself when a string is missing, which would put a
 * raw key like `scan.criteria.Sector.9999` into the prompt. Treat that as
 * "no label" so callers can omit rather than send garbage.
 *
 * An empty or whitespace-only translation is treated the same way. That does
 * collapse two different situations — a key that is absent, and a key that
 * exists but is blank — but both are equally unusable in the prompt, and the
 * caller's only option either way is to omit the entry.
 *
 * @param {Function} localize - `useLocalize()`'s localize.
 * @param {string} key - localization key.
 * @returns {string|null} the trimmed string, or null when `localize` or `key`
 *   is missing, `localize` throws, the value is not a string, the key resolves
 *   to itself (not found), or the value is empty/whitespace-only.
 */
function localizeOrNull(localize, key) {
  if (!localize || !key) return null;
  let text;
  try {
    text = localize(key);
  } catch (e) {
    return null;
  }
  if (typeof text !== 'string') return null;
  const trimmed = text.trim();
  if (trimmed.length === 0 || trimmed === key) return null;
  return trimmed;
}

/**
 * Formats a number to the scan's configured precision and unit.
 *
 * Trailing zeros are trimmed so a threshold of `15` reads `15%` rather than
 * `15.0%`, while a real value keeps its precision (`3.94%`).
 *
 * @returns {string|null} null when the value is not a finite number.
 */
function formatNumeric(scanName, raw, config, matched) {
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n)) {
    // Logged only for a SYMBOL's value, where the input is Syphon data and a
    // non-numeric `filterValue` on a numeric scan means something upstream is
    // wrong. Thresholds come from our own config and are covered by the
    // operator sweep in tests, so warning on those would only add noise.
    if (matched) {
      Logger.warn({
        errorCode: 'E0047',
        message: `Scan summary: ${scanName} value is not a finite number: ${JSON.stringify(
          raw
        )}`
      });
    }
    return null;
  }
  // A zero that encodes "no data" is an absent value, not a real one. Gated on
  // `matched` so it only ever applies to a SYMBOL's value: a zero in a hashtag
  // threshold is a deliberate rule (`>= 0`), and dropping that would silently
  // remove a stated reason from `conditions[]`.
  if (matched && n === 0 && config.zeroIsAbsent) return null;

  const unit =
    matched && typeof config.matchedUnit === 'string'
      ? config.matchedUnit
      : config.unit || '';

  const decimals = typeof config.decimals === 'number' ? config.decimals : 2;

  // `Intl` does the rounding, the trailing-zero trim and the thousands
  // separators in one pass. Deliberately NOT regex-based: a trailing-zero strip
  // (`/\.?0+$/`) and a grouping lookahead (`/\B(?=(\d{3})+(?!\d))/`) both
  // backtrack quadratically on long digit runs (SonarQube S5852).
  //
  // `maximumFractionDigits` with no minimum is exactly the wanted behaviour:
  // a threshold of `15` reads `15`, while a real value keeps its precision
  // (`9,619.02`). Locale is pinned so grouping never varies by environment.
  const text = n.toLocaleString('en-US', {
    maximumFractionDigits: decimals,
    useGrouping: Boolean(config.grouped)
  });

  return `${text}${unit}`;
}

/**
 * Resolves a single CODE to its display label.
 *
 * `Exchange` is the awkward one: its `filterValue` is the compound
 * `<Listed_Exchange>,<SectionClassCode>` (e.g. `166,0111`), so the section is
 * taken from after the comma and resolved with the `.long` suffix — the plain
 * key is the `東P` short form, and the summary wants `東証プライム`.
 *
 * @returns {string|null} null when the code has no label (caller omits it).
 */
function formatCode(raw, config) {
  if (raw === null || raw === undefined) return null;
  let code = String(raw).trim();
  if (code.length === 0) return null;

  if (config.compound) {
    const parts = code.split(',');
    code = parts[parts.length - 1].trim();
  }

  // Scans whose string table is keyed by group, not by raw code.
  if (config.codeGroups || config.codeGroupDefault) {
    code =
      (config.codeGroups && config.codeGroups[code]) ||
      config.codeGroupDefault ||
      code;
  }

  return `${config.optionPrefix}${code}${config.optionSuffix || ''}`;
}

/**
 * Converts one raw scan value into its display representation.
 *
 * @param {boolean} [matched] - true when formatting a symbol's actual value
 *   rather than a hashtag threshold; selects `matchedUnit` where one is set.
 * @returns {string|null} null when absent or unmappable — callers OMIT rather
 *   than sending a placeholder (AC C19978731).
 */
export function formatScanValue(scanName, raw, localize, matched) {
  const config = SCAN_DISPLAY[scanName];
  if (!config) return null;
  if (raw === null || raw === undefined || raw === '') return null;

  if (config.optionPrefix) {
    const key = formatCode(raw, config);
    return key ? localizeOrNull(localize, key) : null;
  }

  return formatNumeric(scanName, raw, config, matched);
}

/** JP metric name for a scan, e.g. `MarketCap` → `時価総額`. */
export function scanLabel(scanName, localize) {
  const config = SCAN_DISPLAY[scanName];
  if (!config) return null;
  return localizeOrNull(localize, config.labelKey);
}

/**
 * Builds `investmentProfile.questions[]`.
 *
 * `answers` holds option CODES (`'A'|'B'|'C'`); the JP text lives in the string
 * table. A question with no answer is skipped — Syphon requires `answer` to be
 * present, and a half-answered profile should not reach the service.
 */
function buildQuestions(answers, localize) {
  const selected = answers || {};

  return INVESTMENT_QUESTIONS.map((question) => {
    const prompt = localizeOrNull(localize, question.promptKey);
    if (!prompt) return null;

    const options = question.options
      .map((option) => localizeOrNull(localize, option.labelKey))
      .filter(Boolean)
      .map((text) => text.replace(OPTION_PREFIX, ''));

    const answerKey = (
      question.options.find((o) => o.code === selected[question.id]) || {}
    ).labelKey;
    const answerText = localizeOrNull(localize, answerKey);
    if (!answerText) return null;

    return {
      id: question.id,
      question: prompt.replace(QUESTION_PREFIX, ''),
      options: options,
      answer: answerText.replace(OPTION_PREFIX, '')
    };
  }).filter(Boolean);
}

/**
 * Converts one popsicle filter condition to its Syphon display form.
 *
 * Returns null when the condition cannot be represented — an unmapped operator,
 * or a value whose codes have no labels. Dropping is safe: `conditions[]` is
 * descriptive context for the prompt, not a filter Syphon re-executes, so an
 * omitted condition costs the summary one stated reason rather than corrupting
 * the result.
 */
function buildCondition(condition, localize) {
  const { scanName, operator, value } = condition;
  const label = scanLabel(scanName, localize);
  if (!label) return null;

  // Negations with a named positive equivalent (SecurityType → 普通株).
  const positive = POSITIVE_FORM[`${scanName}:${operator}`];
  if (positive) {
    const text = localizeOrNull(localize, positive.valueKey);
    if (!text) return null;
    return {
      scan: scanName,
      label: label,
      operator: positive.operator,
      value: text
    };
  }

  const mapped = OPERATOR_MAP[operator];
  if (!mapped) return null;

  if (mapped === 'in') {
    if (!Array.isArray(value)) return null;
    const labels = value
      .map((code) => formatScanValue(scanName, code, localize))
      .filter(Boolean);
    // De-duplicate: TSE Prime is two codes (`166,0111` / `166,0113`) that both
    // resolve to 東証プライム, and a repeated label reads as noise in the prompt.
    const unique = Array.from(new Set(labels));
    if (unique.length === 0) return null;
    return { scan: scanName, label: label, operator: mapped, value: unique };
  }

  const text = formatScanValue(scanName, value, localize);
  if (!text) return null;
  return { scan: scanName, label: label, operator: mapped, value: text };
}

/**
 * Builds `selectedHashtags[]` — `{ tag, conditions[] }` per selected hashtag,
 * in the order the definitions declare them so the prompt is stable.
 */
function buildHashtags(selectedHashtagIds, localize) {
  const ids =
    selectedHashtagIds instanceof Set
      ? selectedHashtagIds
      : new Set(selectedHashtagIds || []);
  if (ids.size === 0) return [];

  return HASHTAG_DEFINITIONS.filter((definition) => ids.has(definition.id))
    .map((definition) => {
      const tag = localizeOrNull(localize, definition.labelKey);
      const conditions = (HASHTAG_FILTER_CONFIG[definition.id] || [])
        .map((condition) => buildCondition(condition, localize))
        .filter(Boolean);
      if (!tag || conditions.length === 0) return null;
      return { tag: tag.replace(TAG_HASH_SPACING, '#'), conditions: conditions };
    })
    .filter(Boolean);
}

/**
 * Builds `symbols[]` — up to 5 entries of `{ symbol, name, matched[] }`.
 *
 * `matched[]` covers the scans the selected hashtags filtered on: those are
 * precisely the values that explain why the symbol is in the result, which is
 * what the summary has to justify.
 *
 * A symbol with no resolvable `name` is dropped rather than sent with an empty
 * one — Syphon 400s on a blank name, and losing one candidate beats losing the
 * whole request.
 */
function buildSymbols(symbolIds, scanNames, scanData, symbolMetadata, localize) {
  const data = scanData || {};
  const metadata = symbolMetadata || {};

  return (symbolIds || [])
    .slice(0, MAX_SUMMARY_SYMBOLS)
    .map((symbolId) => {
      // `state.symbols.data[id]` wraps the payload in `.data`; accept the
      // unwrapped shape too so tests can pass a plain map.
      const entry = metadata[symbolId];
      const meta = (entry && entry.data) || entry;
      const name = meta && (meta.jpShortName || meta.name);

      if (!name) {
        // Syft does not always resolve a symbol — an unmatched entry never gets
        // `receiveSymbolMetaData` and stays loading forever. A bare ticker is
        // not a name a member would recognise, so the candidate is skipped
        // outright rather than renamed or backfilled.
        Logger.error({
          errorCode: 'E0046',
          message: `Scan summary: skipping ${symbolId} — no name resolved`
        });
        return null;
      }

      const matched = scanNames
        .map((scanName) => {
          const scanEntry = (data[scanName.toLowerCase()] || {}).symbols;
          const symbolHit = scanEntry && scanEntry[symbolId];
          const raw = symbolHit && symbolHit.filterValue;
          const value = formatScanValue(scanName, raw, localize, true);
          if (!value) return null;
          const label = scanLabel(scanName, localize);
          if (!label) return null;
          return { scan: scanName, label: label, value: value };
        })
        .filter(Boolean);

      if (matched.length === 0) return null;
      return { symbol: symbolId, name: String(name).trim(), matched: matched };
    })
    .filter(Boolean);
}

/**
 * Assembles the full `POST /scans/summarize` request body.
 *
 * @param {object} params
 * @param {string} params.sessionId - Echoed back by Syphon in `data.sessionId`.
 * @param {{q1:?string,q2:?string}} params.answers - Option codes from state.
 * @param {Set<string>|string[]} params.selectedHashtagIds
 * @param {string[]} params.symbols - Candidate symbol IDs, best-first. Only the
 *   first {@link MAX_SUMMARY_SYMBOLS} are considered; one with no resolved name
 *   is skipped and NOT backfilled — once ordering is market-cap descending, a
 *   lower-ranked symbol must not take a top slot, so a short request is the
 *   honest outcome and the E0046 log says which symbol was lost.
 * @param {object} params.scanData - `state.scans.scanData`.
 * @param {object} params.symbolMetadata - `state.symbols` metadata by symbol ID.
 * @param {Function} params.localize - `useLocalize()`'s localize.
 * @returns {object|null} The request body, or null when it would be invalid
 *   (no answers, no hashtags, or no sendable symbol).
 */
export function buildSummarizeRequest({
  sessionId,
  answers,
  selectedHashtagIds,
  symbols,
  scanData,
  symbolMetadata,
  localize
}) {
  if (!sessionId) return null;

  const questions = buildQuestions(answers, localize);
  if (questions.length !== INVESTMENT_QUESTIONS.length) return null;

  const selectedHashtags = buildHashtags(selectedHashtagIds, localize);
  if (selectedHashtags.length === 0) return null;

  // The scans the selection filtered on, de-duplicated in declaration order.
  const scanNames = Array.from(
    new Set(
      selectedHashtags.reduce(
        (acc, hashtag) =>
          acc.concat(hashtag.conditions.map((condition) => condition.scan)),
        []
      )
    )
  );

  const built = buildSymbols(
    symbols,
    scanNames,
    scanData,
    symbolMetadata,
    localize
  );
  if (built.length === 0) return null;

  return {
    sessionId: sessionId,
    investmentProfile: { questions: questions },
    selectedHashtags: selectedHashtags,
    symbols: built
  };
}

export default buildSummarizeRequest;
